/**
 * The screen an invitation link opens.
 *
 * Rendered ahead of the shell, like PendingDeletion: the person following the
 * link may belong to no organization at all, so the dashboard behind it would
 * be a screen of 401s with the one control that matters buried among them.
 *
 * Three states, and all three are ordinary rather than errors:
 *
 *   signed out          — sign in or create an account, come back here
 *   signed in, matching — one button
 *   signed in, someone else — explain, and offer the way out
 *
 * The third is the one worth getting right. An invitation is bound to its
 * address, so a link forwarded to a colleague, or opened in a browser signed in
 * to a personal account, is refused by the server — and "not found" would send
 * that person hunting for a broken invitation instead of switching accounts.
 */

import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { roleInfo } from "../roles";
import { useSession } from "../session";
import type { InvitationPreview } from "../api/types";

export function AcceptInvitation() {
  const { token = "" } = useParams();
  const { phase, reload, switchOrg, logout } = useSession();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Unscoped, and it works signed out: the screen has to be able to say who the
  // invitation is for before anybody has signed in.
  const { data, error: loadError, loading } = useAsync<InvitationPreview>(
    (signal) => api.get<InvitationPreview>(`/auth/invitations/${token}`, { signal, unscoped: true }),
    [token, phase],
  );

  if (!token) return <Navigate to="/" replace />;

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const joined = await api.post<{ id: string; name: string }>(
        `/auth/invitations/${token}/accept`,
        undefined,
        { unscoped: true },
      );
      // Selected before reloading, so the dashboard opens in the organization
      // just joined rather than wherever it was pointed before.
      switchOrg(joined.id);
      await reload();
      navigate("/messages");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reach the server.");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="centred">
        <span className="oc-body-sm oc-text-secondary">Loading the invitation…</span>
      </div>
    );
  }

  // One answer for unknown, expired, revoked and already accepted — the server
  // does not distinguish them, because telling somebody which of those a token
  // is tells them the token existed.
  if (loadError || !data) {
    return (
      <div className="centred">
        <div className="oc-panel login__card deletion__card">
          <h1 className="oc-headline-md">This invitation cannot be used</h1>
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            The link may have expired, been revoked, or already been accepted. Invitations last
            seven days and work once. Ask whoever invited you for a new one.
          </p>
          <button type="button" className="oc-btn oc-btn--sm" onClick={() => navigate("/")}>
            Go to the dashboard
          </button>
        </div>
      </div>
    );
  }

  const info = roleInfo(data.role);
  const signedOut = data.signed_in_as === undefined;

  return (
    <div className="centred">
      <div className="oc-panel oc-starfield login__card deletion__card">
        <h1 className="oc-headline-md" style={{ marginBottom: 0 }}>
          Join {data.organization_name}
        </h1>
        <p className="oc-body-sm oc-text-secondary" style={{ marginTop: 0 }}>
          You have been invited as <strong>{info.label.toLowerCase()}</strong>
          {info.summary && <> — {info.summary.charAt(0).toLowerCase() + info.summary.slice(1)}</>}
        </p>

        <dl className="settings__facts">
          <dt className="oc-label">Invitation for</dt>
          <dd className="oc-mono-sm">{data.email}</dd>
          <dt className="oc-label">Expires</dt>
          <dd className="oc-body-sm">{new Date(data.expires_at).toLocaleString()}</dd>
        </dl>

        {error && (
          <div className="field-error" role="alert">
            {error}
          </div>
        )}

        {signedOut && (
          <>
            <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
              Sign in as <span className="oc-mono-sm">{data.email}</span> to accept. The
              invitation is tied to that address.
            </p>
            <button
              type="button"
              className="oc-btn oc-btn--primary"
              onClick={() =>
                // The token survives the round trip through login, the way
                // return_to already works everywhere else in the dashboard.
                navigate(`/login?return_to=${encodeURIComponent(`/invitations/${token}`)}`)
              }
            >
              Sign in to accept
            </button>
          </>
        )}

        {!signedOut && data.matches && (
          <button
            type="button"
            className="oc-btn oc-btn--primary"
            disabled={busy}
            onClick={() => void accept()}
          >
            {busy ? "Joining…" : `Join ${data.organization_name}`}
          </button>
        )}

        {!signedOut && !data.matches && (
          <>
            <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
              You are signed in as <span className="oc-mono-sm">{data.signed_in_as}</span>, but
              this invitation was sent to <span className="oc-mono-sm">{data.email}</span>. Sign
              out and sign in as them, or ask for an invitation to the address you use.
            </p>
            <button type="button" className="oc-btn oc-btn--primary" onClick={() => void logout()}>
              Sign out
            </button>
          </>
        )}
      </div>
    </div>
  );
}
